import React from 'react';

export interface TypographyProps {
  /** The HTML element to render */
  component?: React.ElementType;
  /** The variant of the typography */
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'caption'
    | 'overline';
  /** The color of the text */
  color?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'danger'
    | 'success'
    | 'warning'
    | 'inherit';
  /** The alignment of the text */
  align?: 'left' | 'center' | 'right' | 'justify';
  /** Whether the text should be displayed as a block element */
  block?: boolean;
  /** Whether the text should be bold */
  bold?: boolean;
  /** Whether the text should be italic */
  italic?: boolean;
  /** Whether the text should be uppercase */
  uppercase?: boolean;
  /** Whether the text should be truncated with an ellipsis */
  truncate?: boolean;
  /** Whether the text should have a gutter (margin) */
  gutterBottom?: boolean;
  /** Whether the text should be displayed as a paragraph */
  paragraph?: boolean;
  /** Whether the text should be displayed with a line through it */
  lineThrough?: boolean;
  /** Whether the text should be displayed with an underline */
  underline?: boolean;
  /** The start decorator for the text */
  startDecorator?: React.ReactNode;
  /** The end decorator for the text */
  endDecorator?: React.ReactNode;
  /** The children of the typography */
  children?: React.ReactNode;
  /** Additional className for the typography */
  className?: string;
  /** SX props for the typography */
  sx?: React.CSSProperties;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      component,
      variant = 'body1',
      color = 'primary',
      align = 'left',
      block = false,
      bold = false,
      italic = false,
      uppercase = false,
      truncate = false,
      gutterBottom = false,
      paragraph = false,
      lineThrough = false,
      underline = false,
      startDecorator,
      endDecorator,
      children,
      className = '',
      sx = {},
      ...props
    },
    ref
  ) => {
    // Determine the component to render based on variant and component prop
    const Component: React.ElementType =
      component ||
      (variant.startsWith('h')
        ? (variant as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6')
        : paragraph
        ? 'p'
        : variant === 'subtitle1' || variant === 'subtitle2'
        ? 'h6'
        : 'span');

    // Map variant to appropriate text size
    const variantStyles = {
      h1: 'text-4xl font-bold',
      h2: 'text-3xl font-bold',
      h3: 'text-2xl font-bold',
      h4: 'text-xl font-bold',
      h5: 'text-lg font-bold',
      h6: 'text-base font-bold',
      subtitle1: 'text-lg font-medium',
      subtitle2: 'text-base font-medium',
      body1: 'text-base',
      body2: 'text-sm',
      caption: 'text-xs',
      overline: 'text-xs uppercase tracking-wider',
    };

    // Map color to appropriate text color
    const colorStyles = {
      primary: 'text-primary',
      secondary: 'text-secondary',
      tertiary: 'text-tertiary',
      danger: 'text-red-600',
      success: 'text-green-500',
      warning: 'text-amber-600',
      inherit: 'text-inherit',
    };

    // Map alignment to appropriate text alignment
    const alignStyles = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify',
    };

    // Combine all styles
    const styles = [
      variantStyles[variant],
      colorStyles[color],
      alignStyles[align],
      block ? 'block' : 'inline',
      bold ? 'font-bold' : '',
      italic ? 'italic' : '',
      uppercase ? 'uppercase' : '',
      truncate ? 'truncate' : '',
      gutterBottom ? 'mb-2' : '',
      paragraph ? 'mb-4' : '',
      lineThrough ? 'line-through' : '',
      underline ? 'underline' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Component ref={ref as any} className={styles} style={sx} {...props}>
        {startDecorator && (
          <span className="inline-flex mr-1 items-center">
            {startDecorator}
          </span>
        )}
        {children}
        {endDecorator && (
          <span className="inline-flex ml-1 items-center">{endDecorator}</span>
        )}
      </Component>
    );
  }
);

Typography.displayName = 'Typography';

export default Typography;
